import React, { useEffect } from "react";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Container, Box, Button, Grid } from "@mui/material";
import { Step, Stepper, StepLabel, styled, Stack } from "@mui/material";

import Complex from "./Complex";
import Parking from "./Parking";
import Complete from "./Complete";
import Personal from "./Personal";
import VerifyEmail from "./VerifyEmail";
import Confirmation from "./Confirmation";
import { stepSchemas } from "./validationSchema";
import {handleCreateUser,checkEmailExist} from "../handleCreateUser";


const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "#186FE7",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "#186FE7",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 1,
    border: 0,
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : "#969696",
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#f5f5f5",
  zIndex: 1,
  color: "#969696",
  border: "1px solid #969696",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",

  alignItems: "center",
  ...(ownerState.active && {
    backgroundColor: "#186FE7",
    border: "1px solid #fff",
    color: "#fff",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  }),
  ...(ownerState.completed && {
    backgroundColor: "#186FE7",
    border: "1px solid #186FE7",
    color: "#fff",
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
  /**
   * The label displayed in the step icon.
   */
  icon: PropTypes.node,
};

const steps = [
  "Personal Information",
  "Complex Information",
  "Parking Information",
  "Verify Information",
  "Verify Email",
  "Complete",
];

export default function SignUp() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEmailValid, setIsEmailValid] = React.useState(false);


  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [activeStep]);

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const checkEmail = async (email) => {
    if (!email) return;

    try {
      const exists = await checkEmailExist(email);
      setIsEmailValid(!exists);
      if (exists) {
        toast.error("Email already exists.");
      }
    } catch (e) {
      // Never trap the user on step 0 if the existence check fails (e.g. a
      // transient/API error). Let them proceed — Cognito still rejects a
      // true duplicate at sign-up. Previously an error here left
      // isEmailValid=false forever, greying out the Continue button.
      console.error("Email existence check failed:", e);
      setIsEmailValid(true);
    }
  };
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(stepSchemas[activeStep]),
    defaultValues: {
      name: "",
      mail: "",
      phone: "",
      pass: "",
      rePass: "",
      role: "user",
      comName: "",
      comAddress: "",
      comUnit: "",
      comMail: "",
      comPhone: "",
      gate: "",
      fileName: "",
      pSpot: "",
      pNumber: "",
      pNumGuest: "",
      pAdmin: "",
      pHandcap: "",
      pSystem: "",
    },
    mode: "onChange",
  });

  const handleNext = handleSubmit(async (data) => {
    setIsLoading(true);
    if (activeStep === 3) {
      try {
        // Create tenant data from form data
        const tenantData = {
          name: data.name,
          email: data.mail,
        };
        await handleCreateUser(data, tenantData);
        toast.success("Registered Successfully");
        setActiveStep(activeStep + 1);
      } catch (error) {
        toast.error(
          error ||
            error?.message ||
            error?.errors[0]?.message ||
            "Something Went Wrong"
        );
      }
    } else {
      setActiveStep(activeStep + 1);
    }

    setIsLoading(false);
  });

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    goToTop();
  };

  function getStepContent(step) {
    const formData = watch();

    switch (step) {
      case 0:
        return <Personal register={register} errors={errors}  onEmailBlur={checkEmail} setValue={setValue} />;
      case 1:
        return (
          <Complex
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        );
      case 2:
        return (
          <Parking
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        );
      case 3:
        return (
          <Confirmation
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            handleSubmit={handleSubmit}
          />
        );
      case 4:
        return (
          <VerifyEmail
            formData={formData}
            setActiveStep={setActiveStep}
            activeStep={activeStep}
          />
        );
      case 5:
        return <Complete formData={formData} />;

      default:
        throw new Error("Unknown step");
    }
  }

  return (
    <Container>
      <Stack sx={{ width: "100%", marginBottom: "100px" }} spacing={4}>
        <Stepper
          alternativeLabel
          activeStep={activeStep}
          connector={<ColorlibConnector />}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={ColorlibStepIcon}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Stack>
      {getStepContent(activeStep)}
      {activeStep < 4 && (
        <Container maxWidth="md" sx={{ marginBottom: "100px" }}>
          <Grid container>
            <Grid item xs={5}></Grid>
            <Grid item xs={7}>
              <Box sx={{ maxWidth: "400px", display: "flex" }}>
                {activeStep !== 0 && (
                  <Button onClick={handleBack} sx={{ mt: 3, ml: 1 }}>
                    Back
                  </Button>
                )}
                <Button
                  size="large"
                  variant="contained"
                  fullWidth
                  onClick={handleNext}
                  sx={{
                    mt: 3,
                    ml: 1,
                    borderRadius: "8px",
                  }}
                  disabled={isLoading || (!isEmailValid && activeStep === 0)}
                >
                  {activeStep === 3 ? "Complete" : "Continue"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      )}
    </Container>
  );
}
